import { useState, useEffect, useRef, useCallback } from 'react'
import { getBackdropUrl } from '../../lib/backdropUrl'
import { useStartMemoryRound, useFinishMemoryRound } from '../../hooks/useArcade'
import { toast } from '../ui/Toast'
import { triggerHaptic } from '../../lib/haptics'
import ArcadeBack from './ArcadeBack'
import ResultBoard from './ResultBoard'

/**
 * Memory, played on IKB's own backdrop library.
 *
 * GRID: four columns on a phone, six from `sm`. At 375px a fifth column puts
 * cards under ~65px, too small to recognise a backdrop — which defeats the
 * game. Difficulty therefore adds rows, and past 10 pairs it has to come from
 * somewhere other than grid size (a single-sport set, where every image looks
 * alike, is the cheapest real difficulty lever).
 *
 * SCORING: only INFORMED misses cost points — see `isInformedMiss`.
 */

// How long a mismatched pair stays face-up before flipping back.
//
// Scaled by difficulty, because the peek IS the difficulty lever here. Hard's
// 900ms is the right speed — long enough to register two images, short enough
// that you have to commit them rather than study them. Easy and medium get
// more room, since the whole point of those tiers is that the game is helping
// you rather than testing you.
//
// Not a huge spread: much past a second and a mismatch stops feeling like a
// miss and starts feeling like the board is waiting on you.
const PEEK_MS = { easy: 1400, medium: 1150, hard: 900, expert: 900 }
const DEFAULT_PEEK_MS = 1150

const DIFFICULTIES = [
  { key: 'easy', label: 'Easy', pairs: 6 },
  { key: 'medium', label: 'Medium', pairs: 8 },
  { key: 'hard', label: 'Hard', pairs: 10 },
  // Six columns of 30 cards. Offered on DESKTOP ONLY: the board has to fit
  // one screen without scrolling (you cannot remember a card you never saw),
  // and six columns at 375px puts a card under 60px, which is too small to
  // tell two player photos apart. Still listed on mobile rather than hidden,
  // disabled with the reason — a tier you can see exists is a reason to open
  // the app on a laptop.
  { key: 'expert', label: 'Expert', pairs: 20, desktopOnly: true },
]

// Matches the `sm` breakpoint the board's column classes already switch on.
const DESKTOP_MIN_WIDTH = 640

function Timer({ startedAt, running }) {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => tick((n) => n + 1), 250)
    return () => clearInterval(id)
  }, [running])
  const seconds = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return <span className="tabular-nums">{m}:{String(s).padStart(2, '0')}</span>
}

export default function MemoryGame({ variant = 'all', variantCount, onExit }) {
  const [difficulty, setDifficulty] = useState('medium')
  // Where the grid starts on screen, so the height left for it can be
  // measured rather than assumed. Re-read on resize and whenever the round
  // changes, since the header above it can reflow.
  const boardRef = useRef(null)
  const [boardTop, setBoardTop] = useState(null)
  // Tracked, not read once: a desktop user who narrows the window should see
  // Expert turn off, and someone who maximises should see it become
  // available without reloading.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= DESKTOP_MIN_WIDTH,
  )
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const [round, setRound] = useState(null)      // { roundId, cards, pairs, parSeconds }
  const [flipped, setFlipped] = useState([])    // card ids currently face-up this turn (0-2)
  const [matched, setMatched] = useState(new Set())
  const [moves, setMoves] = useState(0)
  const [informedMisses, setInformedMisses] = useState(0)
  const [startedAt, setStartedAt] = useState(null)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  // The beat between clearing the board and seeing the score. Without it the
  // result screen replaced the board on the same frame as the final match —
  // the moment you won was the moment the thing you won at vanished.
  const [celebrating, setCelebrating] = useState(false)

  // Every card id the player has ever had face-up. This is what makes a miss
  // "informed": flipping a card you have never seen is information gathering,
  // not a mistake, so exploring is free and forgetting is not.
  const seen = useRef(new Set())
  const lockUntil = useRef(0)

  // RECALL. Score says how a round went; it does not say how good someone's
  // memory is, because it mixes in speed, board size and luck on cards
  // nobody had seen. This asks one question: the first time it is possible
  // to know where a pair is, did they know?
  //
  // An opportunity is turning over a card whose partner has already been
  // revealed. A hit is turning over that partner next. Only the FIRST
  // opportunity per pair counts — a second attempt measures persistence, and
  // counting it would let someone grind the percentage up by missing
  // repeatedly until they finally land it.
  const recallCounted = useRef(new Set())
  const [recall, setRecall] = useState({ chances: 0, hits: 0 })

  const startRound = useStartMemoryRound()
  const finishRound = useFinishMemoryRound()

  const reset = useCallback(() => {
    setRound(null); setFlipped([]); setMatched(new Set())
    setMoves(0); setInformedMisses(0); setStartedAt(null); setResult(null)
    setCelebrating(false)
    seen.current = new Set()
    recallCounted.current = new Set()
    setRecall({ chances: 0, hits: 0 })
  }, [])

  async function deal(diff) {
    setBusy(true)
    try {
      const data = await startRound.mutateAsync({ difficulty: diff, variant })
      seen.current = new Set()
      recallCounted.current = new Set()
      setRecall({ chances: 0, hits: 0 })
      // Stamp the difficulty onto the round rather than reading the picker's
      // state at flip time: the peek belongs to the round being played, and
      // this stays correct if the picker ever becomes reachable mid-round.
      setRound({ ...data, difficulty: diff })
      setFlipped([]); setMatched(new Set()); setMoves(0); setInformedMisses(0)
      setResult(null); setCelebrating(false)
      setStartedAt(Date.now())
    } catch (err) {
      toast(err.message || 'Could not start a round', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function submit(finalMoves, finalMisses, finalRecall) {
    try {
      const res = await finishRound.mutateAsync({
        roundId: round.roundId, moves: finalMoves, informedMisses: finalMisses,
        recallChances: finalRecall.chances, recallHits: finalRecall.hits,
      })
      setResult(res)
    } catch (err) {
      toast(err.message || 'Could not save your score', 'error')
    }
  }

  function handleFlip(card) {
    if (!round || result || celebrating) return
    if (Date.now() < lockUntil.current) return          // mid-peek
    if (matched.has(card.id)) return
    if (flipped.includes(card.id)) return
    if (flipped.length >= 2) return

    const next = [...flipped, card.id]
    setFlipped(next)

    if (next.length < 2) {
      // First card of the turn. Mark it seen AFTER the informed check below
      // would have run, which is why that check reads `seen` for the SECOND
      // card only.
      seen.current.add(card.id)
      return
    }

    // Second card — this is the guess.
    const [firstId] = next
    const first = round.cards.find((c) => c.id === firstId)
    const isMatch = first && first.pairKey === card.pairKey

    // The penalty lands only when the card being guessed ON was already
    // revealed earlier. At that moment the player can see the first card and
    // has seen the second before, so they knew it would not match. Two
    // unseen cards is a legitimate gamble and costs nothing.
    const wasSeenBefore = seen.current.has(card.id)

    // Was the answer knowable when they turned the first card over? Checked
    // against the partner of the FIRST card, and before this card joins
    // `seen`, so the question is "did you already know where it was".
    const partner = round.cards.find((c) => c.pairKey === first?.pairKey && c.id !== firstId)
    let nextRecall = recall
    if (partner && seen.current.has(partner.id) && !recallCounted.current.has(first.pairKey)) {
      recallCounted.current.add(first.pairKey)
      nextRecall = {
        chances: recall.chances + 1,
        hits: recall.hits + (card.id === partner.id ? 1 : 0),
      }
      setRecall(nextRecall)
    }

    seen.current.add(card.id)

    const nextMoves = moves + 1
    setMoves(nextMoves)

    if (isMatch) {
      const nextMatched = new Set(matched); nextMatched.add(firstId); nextMatched.add(card.id)
      setMatched(nextMatched)
      setFlipped([])
      if (nextMatched.size === round.cards.length) {
        // Board cleared. Hold it on screen — a haptic and the whole grid lit
        // up — before the score replaces it. 1.4s read as an interruption on
        // the way to somewhere else; at 3s the finished board is its own
        // moment, which is what someone who just cleared it wants to look at.
        setCelebrating(true)
        triggerHaptic('Heavy')
        setTimeout(() => submit(nextMoves, informedMisses, nextRecall), 3000)
      }
      return
    }

    const nextMisses = wasSeenBefore ? informedMisses + 1 : informedMisses
    if (wasSeenBefore) setInformedMisses(nextMisses)

    const peekMs = PEEK_MS[round.difficulty] ?? DEFAULT_PEEK_MS
    lockUntil.current = Date.now() + peekMs
    setTimeout(() => { setFlipped([]) }, peekMs)
  }

  // Four columns on a phone, five from `sm` once there are more than 16
  // cards. Kept as a number too, because the board has to be sized from the
  // VIEWPORT HEIGHT and that needs the row count.
  const expertBoard = round?.difficulty === 'expert'
  const wideBoard = round && round.cards.length > 16
  // Expert is EIGHT wide from `sm`. It stays four on a phone so a forced
  // expert round is merely long rather than unreadable.
  useEffect(() => {
    function measure() {
      if (!boardRef.current) return
      // VIEWPORT-relative on purpose: this is compared against
      // window.innerHeight to find the room left below, so adding scrollY
      // would subtract the scroll twice and shrink the board as you scroll.
      setBoardTop(boardRef.current.getBoundingClientRect().top)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [round?.id, round?.cards?.length])

  const cols = expertBoard ? 'grid-cols-4 sm:grid-cols-8'
    : wideBoard ? 'grid-cols-4 sm:grid-cols-5' : 'grid-cols-4 sm:grid-cols-4'

  // A memory board you cannot see all of is not a memory game: you cannot
  // remember a card you never saw, and scrolling between flips means the
  // top half is out of sight exactly when you need to recall it. Hard on a
  // desktop was four rows of 240px cards — the bottom two rows were below
  // the fold.
  //
  // Cards are square, so capping the WIDTH caps the height: with `rows` rows
  // in the space left under the nav, each card can be that tall, and the
  // board is that times the column count. The cap only ever binds when the
  // screen is wide relative to its height, which is every desktop and no
  // phone — on mobile the computed width exceeds the page, so nothing
  // changes there.
  const boardCols = expertBoard ? 8 : wideBoard ? 5 : 4
  const boardRows = round ? Math.ceil(round.cards.length / boardCols) : 1
  // Height decides the card size — the board has to fit one screen — so the
  // only way to use a wide window is more COLUMNS, not bigger cards. Eight
  // columns of the same card is a third more board than six.
  //
  // Expert also breaks out of the arcade's max-w-7xl: at 8 x 200px the board
  // wants 1600px and the container would clip it to ~1216, shrinking the
  // cards on exactly the big screens the tier exists for. 100vw - 4rem keeps
  // a margin and never exceeds the window.
  // MEASURED, not guessed. This used to reserve a flat 15rem for everything
  // above and below the board, which is a number that was right once: the
  // navbar, the panel padding and the Quit/score row do not actually add up
  // to 240px, so the board came out short and left a band of dead space
  // under it on a desktop — visible, and the whole point of the tier is the
  // pictures being big.
  //
  // boardTop is where the grid actually starts, read off the DOM, so the
  // allowance is exact and follows any chrome change for free.
  const GUTTER_PX = 20
  const fallbackHeight = `calc((100dvh - 15rem) / ${boardRows} * ${boardCols})`
  const measuredHeight = boardTop != null
    ? `${Math.max(0, (window.innerHeight - boardTop - GUTTER_PX)) / boardRows * boardCols}px`
    : null
  const heightCap = measuredHeight || fallbackHeight
  const widthCap = expertBoard
    ? `min(calc(100vw - 4rem), ${boardCols * 260}px)`
    : `${boardCols * 200}px`
  const boardMaxWidth = `min(${heightCap}, ${widthCap})`

  // ── Setup ─────────────────────────────────────────────────────────────
  if (!round) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <ArcadeBack onClick={onExit} />
          <h3 className="font-display text-lg text-text-primary">Memory</h3>
        </div>
        <p className="text-sm text-text-secondary">
          Match every pair. Flipping a card you have not seen before is free —
          you only lose points for missing a pair you had already uncovered.
        </p>
        {/* Three equal START buttons, none pre-selected.
            `difficulty` used to default to medium and highlight it, which
            read as "medium is chosen, now confirm" — but tapping any of the
            three deals immediately, so the highlight was describing a step
            that doesn't exist. It is still tracked, purely so Play again can
            repeat the tier you just played.

            The guard stays in case a deck ever ships with fewer images than
            Hard needs; the library has 220, so today it never fires. */}
        <div className="flex gap-2">
          {DIFFICULTIES.map((d) => {
            const fits = !variantCount || variantCount >= d.pairs
            const blockedOnMobile = d.desktopOnly && !isDesktop
            const playable = fits && !blockedOnMobile
            return (
              <button
                key={d.key}
                onClick={() => { setDifficulty(d.key); deal(d.key) }}
                disabled={busy || !playable}
                title={blockedOnMobile ? 'Expert needs a wider screen' : fits ? undefined : `Only ${variantCount} images available`}
                className="flex-1 rounded-xl border border-text-primary/20 px-3 py-3 text-sm font-semibold text-text-secondary transition-colors hover:border-accent hover:bg-accent/10 hover:text-text-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {d.label}
                <span className="block text-[11px] font-normal text-text-muted">
                  {blockedOnMobile ? 'desktop only' : fits ? `${d.pairs} pairs` : 'too few images'}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Result ────────────────────────────────────────────────────────────
  if (result) {
    const b = result.breakdown
    return (
      <div className="space-y-4">
        <ArcadeBack onClick={onExit} />
        <h3 className="font-display text-2xl text-text-primary text-center">
          {result.isPersonalBest ? 'New personal best' : 'Round complete'}
        </h3>
        <div className="font-display text-5xl text-accent tabular-nums text-center">{result.score}</div>
        {/* Shown as a breakdown, not a bare number — the scoring rule is the
            game's whole strategy, so hiding it would hide the strategy. */}
        <div className="mx-auto max-w-xs text-sm text-text-secondary space-y-1">
          <div className="flex justify-between"><span>{round.pairs} pairs</span><span className="tabular-nums">+{b.base}</span></div>
          <div className="flex justify-between">
            <span>{informedMisses} informed {informedMisses === 1 ? 'miss' : 'misses'}</span>
            <span className="tabular-nums text-incorrect">{b.penalty ? `−${b.penalty}` : '0'}</span>
          </div>
          <div className="flex justify-between">
            <span>{b.elapsedSeconds}s {b.timeBonus > 0 ? `(under ${b.parSeconds}s par)` : `(par ${b.parSeconds}s)`}</span>
            <span className="tabular-nums text-correct">{b.timeBonus ? `+${b.timeBonus}` : '0'}</span>
          </div>
          {/* Scores nothing, and that is the point: it measures recall on its
              own, with speed and board size taken out. */}
          {recall.chances > 0 && (
            <div className="flex justify-between border-t border-text-primary/10 pt-1 mt-1">
              <span>Knew it first chance</span>
              <span className="tabular-nums text-text-primary">
                {recall.hits}/{recall.chances} · {Math.round((recall.hits / recall.chances) * 100)}%
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-center pt-2">
          <button onClick={() => deal(difficulty)} className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white">Play again</button>
          <button onClick={reset} className="rounded-xl border border-text-primary/20 px-5 py-2.5 text-sm font-semibold text-text-secondary">Change difficulty</button>
        </div>

        <ResultBoard game="memory" />
      </div>
    )
  }

  // ── Board ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <ArcadeBack onClick={reset} label="Quit" />
        {celebrating ? (
          // Says the thing out loud for the second the board is held. The
          // clock stops here too — it stopped when the last pair matched,
          // and watching it keep running would undercut the moment.
          <span className="font-display text-lg text-correct">Board cleared</span>
        ) : (
          <div className="flex items-center gap-4 text-text-secondary">
            <span>{matched.size / 2}/{round.pairs}</span>
            <span className="text-text-muted">{moves} moves</span>
            <Timer startedAt={startedAt} running={!result} />
          </div>
        )}
      </div>

      <div
        ref={boardRef}
        className={`grid ${cols} gap-2 -mx-2 sm:mx-auto`}
        // width:100% lets the breakout cap above actually apply — without it
        // the grid is only ever as wide as the container allows.
        style={{ maxWidth: boardMaxWidth, width: expertBoard ? '100vw' : undefined }}
      >
        {round.cards.map((card) => {
          const isUp = flipped.includes(card.id) || matched.has(card.id)
          const isMatched = matched.has(card.id)
          return (
            <button
              key={card.id}
              onClick={() => handleFlip(card)}
              aria-label={isUp ? card.label : 'Hidden card'}
              className={`relative aspect-square rounded-lg overflow-hidden border transition-all ${
                celebrating
                  ? 'border-correct scale-[0.97] opacity-100'
                  : isMatched
                  ? 'border-correct/60 opacity-60'
                  : isUp
                  ? 'border-accent'
                  : 'border-text-primary/15 bg-bg-card hover:border-text-primary/35'
              }`}
            >
              {isUp ? (
                <img
                  src={getBackdropUrl(card.filename)}
                  alt={card.label}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center font-display text-lg text-text-muted">?</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
